// hooks/useOrders.js - With caching
import { useState, useEffect } from 'react';
import { dbOnValue, dbPush, dbUpdate, dbGet, sanitizeFirebasePath, cleanFirebaseData } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { dataCache } from '../contexts/DataCache';

const ORDERS_CACHE_KEY = 'cached_orders';
const STAFF_CACHE_KEY = 'cached_staff';

const getSafeUserId = (user) => {
  if (!user) return 'anonymous_user';
  const userId = user.$id || user.uid || user.id;
  if (userId && typeof userId === 'string') return sanitizeFirebasePath(userId);
  if (user.email) return sanitizeFirebasePath(user.email);
  return 'anonymous_user';
};

// Generate a random 6-digit OTP
const generateOTP = () => {
  return Math.floor(100000 + Math.random() * 900000).toString();
};

export const useOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [staffList, setStaffList] = useState([]);
  const { user } = useAuth();

  // Load staff list with cache
  useEffect(() => {
    let isMounted = true;

    // Try to load from cache
    const cachedStaff = dataCache.get(STAFF_CACHE_KEY);
    if (cachedStaff && Array.isArray(cachedStaff)) {
      setStaffList(cachedStaff);
    }

    const unsubscribe = dbOnValue('users', (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const staff = Object.entries(data)
          .filter(([_, userData]) => userData.role === 'staff')
          .map(([id, userData]) => ({ id, firebaseKey: id, ...userData }));
        
        dataCache.set(STAFF_CACHE_KEY, staff);
        
        if (isMounted) {
          setStaffList(staff);
        }
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  // Load orders with cache
  useEffect(() => {
    let isMounted = true;

    // Try to load from cache
    const cachedOrders = dataCache.get(ORDERS_CACHE_KEY);
    if (cachedOrders && Array.isArray(cachedOrders) && cachedOrders.length > 0) {
      console.log('[useOrders] Loaded from cache:', cachedOrders.length, 'orders');
      setOrders(cachedOrders);
      setLoading(false);
    }

    const unsubscribe = dbOnValue('orders', (snapshot) => {
      const data = snapshot.val();
      let orderList = [];
      
      if (data) {
        orderList = Object.entries(data).map(([firebaseKey, order]) => ({
          firebaseKey: firebaseKey,
          id: order.id || firebaseKey,
          ...order
        }));
        orderList.sort((a, b) => new Date(b.orderDate) - new Date(a.orderDate));
        
        // Update cache
        dataCache.set(ORDERS_CACHE_KEY, orderList);
      } else {
        dataCache.set(ORDERS_CACHE_KEY, []);
      }
      
      if (isMounted) {
        setOrders(orderList);
        setLoading(false);
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [refreshKey]);

  // Helper to find order by either id or firebaseKey
  const findOrder = (identifier) => {
    return orders.find(o => 
      o.id === identifier || 
      o.firebaseKey === identifier ||
      o.orderId === identifier
    );
  };

  // Place new order
  const placeOrder = async (orderData) => {
    const safeUserId = getSafeUserId(user);
    const orderId = `ORD${Math.random().toString(36).substring(2, 10).toUpperCase()}`;
    
    const deliveryOTP = generateOTP();
    const cancellationOTP = orderData.paymentMethod === 'Cash on Delivery' ? generateOTP() : null;
    const orderStatus = 'pending';
    
    const newOrder = {
      id: orderId,
      ...orderData,
      userId: safeUserId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      orderStatus: orderStatus,
      assignedTo: null,
      assignedToId: null,
      assignedAt: null,
      deliveryTime: null,
      actualDeliveryTime: null,
      deliveryOTP: null,
      cancellationOTP: cancellationOTP,
      otpVerified: false,
      paymentStatus: orderData.paymentMethod === 'Cash on Delivery' ? 'pending' : 'completed',
      statusHistory: [{
        status: 'pending',
        timestamp: new Date().toISOString(),
        message: orderData.paymentMethod === 'Cash on Delivery' 
          ? 'Order placed successfully. Awaiting admin confirmation.'
          : 'Order placed successfully. Payment received.'
      }]
    };
    
    const orderRef = await dbPush('orders', newOrder);
    
    // Clear cache to force refresh
    dataCache.clear(ORDERS_CACHE_KEY);
    setRefreshKey(prev => prev + 1);
    
    return { 
      firebaseKey: orderRef.key, 
      id: orderId, 
      ...newOrder, 
      cancellationOTP 
    };
  };

  // Admin confirms order
  const confirmOrder = async (identifier) => {
    const order = findOrder(identifier);
    if (!order) return { success: false, message: 'Order not found' };
    
    const firebaseKey = order.firebaseKey;
    
    if (order.orderStatus !== 'pending') {
      return { success: false, message: `Order is already ${order.orderStatus}` };
    }
    
    const statusHistory = [...(order.statusHistory || []), {
      status: 'confirmed',
      timestamp: new Date().toISOString(),
      message: 'Order confirmed by admin'
    }];
    
    await dbUpdate(`orders/${firebaseKey}`, {
      orderStatus: 'confirmed',
      statusHistory,
      updatedAt: new Date().toISOString()
    });
    
    dataCache.clear(ORDERS_CACHE_KEY);
    setRefreshKey(prev => prev + 1);
    return { success: true, message: 'Order confirmed successfully' };
  };

  // Process order
  const processOrder = async (identifier) => {
    const order = findOrder(identifier);
    if (!order) return { success: false, message: 'Order not found' };
    
    const firebaseKey = order.firebaseKey;
    
    if (order.orderStatus !== 'confirmed') {
      return { success: false, message: `Order must be confirmed before processing` };
    }
    
    const statusHistory = [...(order.statusHistory || []), {
      status: 'processing',
      timestamp: new Date().toISOString(),
      message: 'Order is being processed'
    }];
    
    await dbUpdate(`orders/${firebaseKey}`, {
      orderStatus: 'processing',
      statusHistory,
      updatedAt: new Date().toISOString()
    });
    
    dataCache.clear(ORDERS_CACHE_KEY);
    setRefreshKey(prev => prev + 1);
    return { success: true, message: 'Order is now being processed' };
  };

  // Dispatch order with delivery time
  const dispatchOrder = async (identifier, deliveryTime) => {
    const order = findOrder(identifier);
    if (!order) return { success: false, message: 'Order not found' };
    
    const firebaseKey = order.firebaseKey;
    
    if (order.orderStatus !== 'processing') {
      return { success: false, message: `Order must be processing before dispatch` };
    }
    
    const deliveryOTP = generateOTP();
    
    const statusHistory = [...(order.statusHistory || []), {
      status: 'out',
      timestamp: new Date().toISOString(),
      message: `Order out for delivery. Expected: ${deliveryTime}.`
    }];
    
    await dbUpdate(`orders/${firebaseKey}`, {
      orderStatus: 'out',
      deliveryTime: deliveryTime,
      deliveryOTP: deliveryOTP,
      statusHistory,
      updatedAt: new Date().toISOString()
    });
    
    dataCache.clear(ORDERS_CACHE_KEY);
    setRefreshKey(prev => prev + 1);
    return { success: true, message: `Order dispatched for delivery`, otp: deliveryOTP };
  };

  // Verify delivery OTP and mark as delivered
  const verifyDeliveryOTP = async (identifier, otp) => {
    const order = findOrder(identifier);
    if (!order) return { success: false, message: 'Order not found' };
    
    const firebaseKey = order.firebaseKey;
    
    if (order.orderStatus !== 'out') {
      return { success: false, message: `Order must be out for delivery. Current status: ${order.orderStatus}` };
    }
    
    if (order.deliveryOTP && order.deliveryOTP !== otp) {
      return { success: false, message: 'Invalid OTP. Please check and try again.' };
    }
    
    const statusHistory = [...(order.statusHistory || []), {
      status: 'delivered',
      timestamp: new Date().toISOString(),
      message: `OTP verified: Order delivered successfully`
    }];
    
    await dbUpdate(`orders/${firebaseKey}`, {
      orderStatus: 'delivered',
      statusHistory,
      actualDeliveryTime: new Date().toISOString(),
      otpVerified: true,
      updatedAt: new Date().toISOString()
    });
    
    dataCache.clear(ORDERS_CACHE_KEY);
    setRefreshKey(prev => prev + 1);
    return { success: true, message: 'Order delivered successfully!' };
  };

  // Mark order as delivered without OTP (for admin override)
  const deliverOrder = async (identifier) => {
    const order = findOrder(identifier);
    if (!order) return { success: false, message: 'Order not found' };
    
    const firebaseKey = order.firebaseKey;
    
    if (order.orderStatus !== 'out') {
      return { success: false, message: `Order must be out for delivery. Current status: ${order.orderStatus}` };
    }
    
    const statusHistory = [...(order.statusHistory || []), {
      status: 'delivered',
      timestamp: new Date().toISOString(),
      message: 'Order marked as delivered by admin'
    }];
    
    await dbUpdate(`orders/${firebaseKey}`, {
      orderStatus: 'delivered',
      statusHistory,
      actualDeliveryTime: new Date().toISOString(),
      otpVerified: true,
      updatedAt: new Date().toISOString()
    });
    
    dataCache.clear(ORDERS_CACHE_KEY);
    setRefreshKey(prev => prev + 1);
    return { success: true, message: 'Order marked as delivered' };
  };

  // Resend delivery OTP
  const resendDeliveryOTP = async (identifier) => {
    const order = findOrder(identifier);
    if (!order) return { success: false, message: 'Order not found' };
    
    const firebaseKey = order.firebaseKey;
    
    if (order.orderStatus !== 'out') {
      return { success: false, message: 'OTP can only be resent for orders out for delivery' };
    }
    
    const newOTP = generateOTP();
    
    await dbUpdate(`orders/${firebaseKey}`, {
      deliveryOTP: newOTP,
      updatedAt: new Date().toISOString()
    });
    
    dataCache.clear(ORDERS_CACHE_KEY);
    setRefreshKey(prev => prev + 1);
    return { success: true, message: 'New OTP sent to customer', otp: newOTP };
  };

  // Get delivery OTP
  const getDeliveryOTP = async (identifier) => {
    const order = findOrder(identifier);
    if (!order) return null;
    return order.deliveryOTP;
  };

  // Reject order
  const rejectOrder = async (identifier, reason = 'Order rejected by admin') => {
    const order = findOrder(identifier);
    if (!order) return { success: false, message: 'Order not found' };
    
    const firebaseKey = order.firebaseKey;
    
    if (!['pending', 'confirmed'].includes(order.orderStatus)) {
      return { success: false, message: 'Cannot reject order at this stage' };
    }
    
    const statusHistory = [...(order.statusHistory || []), {
      status: 'cancelled',
      timestamp: new Date().toISOString(),
      message: reason
    }];
    
    await dbUpdate(`orders/${firebaseKey}`, {
      orderStatus: 'cancelled',
      statusHistory,
      rejectionReason: reason,
      updatedAt: new Date().toISOString()
    });
    
    dataCache.clear(ORDERS_CACHE_KEY);
    setRefreshKey(prev => prev + 1);
    return { success: true, message: 'Order rejected' };
  };

  // Cancel order by customer
  const cancelOrder = async (identifier, otp) => {
    const order = findOrder(identifier);
    if (!order) return { success: false, message: 'Order not found' };
    
    const firebaseKey = order.firebaseKey;

    if (order.paymentMethod !== 'Cash on Delivery') {
      return { success: false, message: 'Online payment orders cannot be cancelled. Contact support for refund.' };
    }

    if (!['pending', 'confirmed'].includes(order.orderStatus)) {
      return { success: false, message: 'Order cannot be cancelled at this stage' };
    }

    const orderTime = new Date(order.orderDate).getTime();
    const currentTime = new Date().getTime();
    const minutesPassed = (currentTime - orderTime) / (1000 * 60);
    
    if (minutesPassed > 5) {
      return { success: false, message: 'Cancellation window closed (5 minutes only).' };
    }

    if (order.cancellationOTP && otp === order.cancellationOTP) {
      const statusHistory = [...(order.statusHistory || []), {
        status: 'cancelled',
        timestamp: new Date().toISOString(),
        message: 'Order cancelled by customer'
      }];
      
      await dbUpdate(`orders/${firebaseKey}`, {
        orderStatus: 'cancelled',
        statusHistory,
        updatedAt: new Date().toISOString()
      });
      
      dataCache.clear(ORDERS_CACHE_KEY);
      setRefreshKey(prev => prev + 1);
      return { success: true, message: 'Order cancelled successfully' };
    }
    return { success: false, message: 'Invalid OTP' };
  };

  // Assign order to staff
  const assignOrderToStaff = async (identifier, staffId) => {
    const order = findOrder(identifier);
    if (!order) return { success: false, message: 'Order not found' };
    
    const firebaseKey = order.firebaseKey;
    
    const staff = staffList.find(s => s.id === staffId);
    const staffName = staff?.name || staffId;
    const staffEmail = staff?.email || '';
    const staffFirebaseKey = staff?.firebaseKey || staffId;
    
    await dbUpdate(`orders/${firebaseKey}`, {
      assignedTo: staffName,
      assignedToId: staffFirebaseKey,
      assignedToEmail: staffEmail,
      assignedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    
    dataCache.clear(ORDERS_CACHE_KEY);
    setRefreshKey(prev => prev + 1);
    return { success: true, message: `Order assigned to ${staffName}` };
  };

  // Generic status update
  const updateOrderStatus = async (identifier, status, message = '', deliveryTime = null) => {
    const order = findOrder(identifier);
    if (!order) return { success: false, message: 'Order not found' };
    
    const firebaseKey = order.firebaseKey;
    
    const validTransitions = {
      'pending': ['confirmed', 'cancelled'],
      'confirmed': ['processing', 'cancelled'],
      'processing': ['out', 'cancelled'],
      'out': ['delivered', 'cancelled'],
      'delivered': [],
      'cancelled': []
    };
    
    if (!validTransitions[order.orderStatus]?.includes(status)) {
      return { success: false, message: `Cannot change from ${order.orderStatus} to ${status}` };
    }
    
    const statusHistory = [...(order.statusHistory || []), {
      status,
      timestamp: new Date().toISOString(),
      message: message || `Order ${status}`
    }];
    
    const updateData = {
      orderStatus: status,
      statusHistory,
      updatedAt: new Date().toISOString()
    };
    
    if (status === 'out' && deliveryTime) {
      updateData.deliveryTime = deliveryTime;
      updateData.deliveryOTP = generateOTP();
    }
    if (status === 'delivered') {
      updateData.actualDeliveryTime = new Date().toISOString();
      updateData.otpVerified = true;
    }
    
    await dbUpdate(`orders/${firebaseKey}`, updateData);
    dataCache.clear(ORDERS_CACHE_KEY);
    setRefreshKey(prev => prev + 1);
    return { success: true, message: `Order ${status}` };
  };

  // Helper functions
  const getStaffList = () => staffList;
  
  const getOrderById = (identifier) => {
    return findOrder(identifier);
  };
  
  const getUserOrders = () => {
    if (!user) return [];
    const safeUserId = getSafeUserId(user);
    const userOrders = orders.filter(o => o.userId === safeUserId);
    return userOrders.sort((a, b) => new Date(b.orderDate) - new Date(a.orderDate));
  };
  
  const getStaffOrders = (staffId) => {
    return orders.filter(o => o.assignedToId === staffId);
  };

  const getOrdersForDelivery = () => {
    return orders.filter(o => o.orderStatus === 'out');
  };

  const getActiveOrders = () => {
    if (!user) return [];
    const safeUserId = getSafeUserId(user);
    return orders.filter(o => 
      o.userId === safeUserId && 
      !['delivered', 'cancelled'].includes(o.orderStatus)
    );
  };

  const getRevenueAnalytics = () => {
    const today = new Date(); 
    today.setHours(0, 0, 0, 0);
    const startOfWeek = new Date(today); 
    startOfWeek.setDate(today.getDate() - today.getDay());
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    
    const todayOrders = orders.filter(o => new Date(o.orderDate) >= today);
    const weekOrders = orders.filter(o => new Date(o.orderDate) >= startOfWeek);
    const monthOrders = orders.filter(o => new Date(o.orderDate) >= startOfMonth);
    
    const todayRevenue = todayOrders.filter(o => o.orderStatus === 'delivered').reduce((s, o) => s + (o.totalAmount || 0), 0);
    const weekRevenue = weekOrders.filter(o => o.orderStatus === 'delivered').reduce((s, o) => s + (o.totalAmount || 0), 0);
    const monthRevenue = monthOrders.filter(o => o.orderStatus === 'delivered').reduce((s, o) => s + (o.totalAmount || 0), 0);
    
    const statusCounts = {
      pending: orders.filter(o => o.orderStatus === 'pending').length,
      confirmed: orders.filter(o => o.orderStatus === 'confirmed').length,
      processing: orders.filter(o => o.orderStatus === 'processing').length,
      out: orders.filter(o => o.orderStatus === 'out').length,
      delivered: orders.filter(o => o.orderStatus === 'delivered').length,
      cancelled: orders.filter(o => o.orderStatus === 'cancelled').length
    };
    
    const totalRevenue = orders.filter(o => o.orderStatus === 'delivered').reduce((s, o) => s + (o.totalAmount || 0), 0);
    const totalOrders = orders.length;
    const pendingDeliveries = orders.filter(o => o.orderStatus === 'out').length;
    const completionRate = totalOrders ? (statusCounts.delivered / totalOrders) * 100 : 0;
    
    const dailyData = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      date.setHours(0, 0, 0, 0);
      const nextDay = new Date(date);
      nextDay.setDate(date.getDate() + 1);
      const dayOrders = orders.filter(o => {
        const od = new Date(o.orderDate);
        return od >= date && od < nextDay;
      });
      dailyData.push({
        date: date.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric' }),
        orders: dayOrders.length,
        revenue: dayOrders.filter(o => o.orderStatus === 'delivered').reduce((s, o) => s + (o.totalAmount || 0), 0)
      });
    }
    
    return {
      todayOrders: todayOrders.length,
      todayRevenue,
      weekOrders: weekOrders.length,
      weekRevenue,
      monthOrders: monthOrders.length,
      monthRevenue,
      totalOrders,
      totalRevenue,
      pendingDeliveries,
      completionRate,
      statusCounts,
      dailyData
    };
  };

  // Force refresh
  const refreshOrders = () => {
    dataCache.clear(ORDERS_CACHE_KEY);
    setRefreshKey(prev => prev + 1);
  };

  return {
    orders,
    loading,
    staffList,
    placeOrder,
    confirmOrder,
    processOrder,
    dispatchOrder,
    deliverOrder,
    verifyDeliveryOTP,
    resendDeliveryOTP,
    getDeliveryOTP,
    getOrdersForDelivery,
    getActiveOrders,
    updateOrderStatus,
    cancelOrder,
    rejectOrder,
    assignOrderToStaff,
    getStaffList,
    getOrderById,
    getUserOrders,
    getStaffOrders,
    getRevenueAnalytics,
    refreshKey,
    refreshOrders
  };
};